import { useVendor } from '@/core/globalState/vendor'
import { isSameAddressPostalCode } from '@/core/utils/address'
import { FormattedPostalCode } from '@/screens/Checkout/components'
import { Badge, useDrawer } from '@/ui/components'
import { csx, Text, Tooltip } from '@vtex/admin-ui'
import React, { useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  LocalizationProvider,
  useLocalizationState,
} from '@vtexlab/assisted-sales-localization'
import { useOrderForm } from '@/core/requests/checkout'
import { SearchLocalizationProvider } from '@/screens/Search/components/SearchLocation/SearchLocationDrawer/context/SearchLocalizationContext'
import { SearchLocationDrawer } from '@/screens/Search/components'
import { WarningIcon } from '@/core/components/WarningIcon'

export const getAddressText = (
  address: Pick<Address, 'postalCode' | 'neighborhood' | 'city'>,
  storePostalCode?: string,
  storeName?: string
) => {
  const isStoreAddress = address.postalCode
    ? isSameAddressPostalCode(storePostalCode ?? '', address.postalCode)
    : false

  let addressText = ''

  if (isStoreAddress && storeName) {
    addressText = storeName
  } else {
    addressText = `${address.neighborhood}, ${address.city}`
  }

  return {
    postalCode: address.postalCode ?? '',
    addressText,
    isStoreAddress,
  }
}

const ShippingToStoreWarning = () => {
  const intl = useIntl()

  return (
    <Tooltip
      text={intl.formatMessage({ id: 'pdp.sku-availability.location.warning' })}
    >
      <WarningIcon />
    </Tooltip>
  )
}

type ProductSkuSimulationLocationProps = {
  address: Address
}

export const ProductSkuSimulationLocation = ({
  address,
}: ProductSkuSimulationLocationProps) => {
  const { vendor } = useVendor()
  const { postalCode, addressText, isStoreAddress } = getAddressText(
    address,
    vendor.store?.postalCode,
    vendor.store?.name
  )

  const { orderForm } = useOrderForm()
  const localizationState = useLocalizationState({
    countryCode: orderForm?.storePreferencesData?.countryCode,
  })

  const drawer = useDrawer({ route: '#/change-address-drawer' })

  const handleChangeLocation = useCallback(() => {
    drawer.open()
  }, [drawer])

  return (
    <LocalizationProvider value={localizationState}>
      <div
        className={csx({
          display: 'grid',
          padding: '$space-5',
          gridTemplateColumns: '1fr auto',
          alignItems: 'center',
        })}
      >
        <div>
          {/* TODO: what should we show if there's no postal code? */}
          <Text
            variant="action1"
            className={csx({
              display: 'flex',
              alignItems: 'center',
              gap: '$space-1',
            })}
          >
            <FormattedPostalCode value={postalCode} />
            {isStoreAddress && <ShippingToStoreWarning />}
          </Text>
          <Text variant="action2" tone="secondary">
            {addressText}
          </Text>
        </div>
        <Badge onClick={handleChangeLocation}>
          <FormattedMessage id="change" />
        </Badge>
      </div>

      <SearchLocalizationProvider>
        <SearchLocationDrawer {...drawer} />
      </SearchLocalizationProvider>
    </LocalizationProvider>
  )
}
